/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

/**
 * WASM-Compatible API Exports for Viz-Client Integration
 *
 * This file provides a TypeScript implementation of the WASM C++ animation API.
 * It exposes the same function signatures that viz-client expects from the
 * RuntimeWebModule (WASM), allowing us to swap out the WASM implementation
 * with pure TypeScript behind a feature flag.
 *
 * INTEGRATION APPROACH (Phase 3A):
 * - This module is exposed as "RuntimeWebModule" (same as WASM)
 * - Feature flag FeatureFlagIds.AnimationWithTsModule controls which module loads
 * - When flag is ON, viz-client loads THIS module instead of WASM
 * - When flag is ON, NO WASM code is executed whatsoever
 * - Viz-client code (TabAnimModuleExports.cs, AnimationUtil.cs) remains unchanged
 *
 * WASM API REFERENCE:
 * See: viz-client/scriptsharp/src/TabAnimWebModule/TabAnimModuleExports.cs
 *
 * PORT NOTES:
 * - C++ uses binary datastores (buffer pointers), TypeScript uses HUTT JSON directly
 * - C++ marshals to HUTT on heap, TypeScript returns JavaScript objects
 * - No malloc/free needed in TypeScript (JavaScript garbage collection)
 * - AnimationPlayStyle mapping: 0=Synced, 1=Phased, 2=InheritStyle
 */

import { AnimationPlayStyle } from './AnimationPlayStyle';
import { animationStyleFromInt } from './AnimationStyle';
import { MarkAnimationController, tick } from './MarkAnimationController';

// ============================================
// Constants
// ============================================

/**
 * Default base duration for animations in milliseconds.
 * This matches the C++ WASM default and is multiplied by the duration parameter
 * passed from viz-client to get the final animation duration.
 */
const DEFAULT_ANIMATION_DURATION_MS = 750;

// ============================================
// WASM-Compatible API Functions
// ============================================

/**
 * Create a new MarkAnimationController.
 *
 * WASM equivalent: _CreateMarkAnimationController()
 *
 * @returns Handle (numeric ID) for the controller
 */
export function _CreateMarkAnimationController(): number {
  const handle = MarkAnimationController.createController();
  return handle;
}

/**
 * Delete a MarkAnimationController.
 *
 * WASM equivalent: _DeleteMarkAnimationController(int handle)
 *
 * @param handle - The controller handle
 */
export function _DeleteMarkAnimationController(handle: number): void {
  MarkAnimationController.deleteController(handle);
}

/**
 * Start an animation with a datastore.
 *
 * WASM equivalent: _AnimateDatastore(int handle, int buffer_ptr, int buffer_size, int style, float duration)
 *
 * In WASM version:
 * - buffer_ptr points to binary datastore on WASM heap
 * - buffer_size is the size in bytes
 * - Returns true if animation started
 *
 * In TypeScript version:
 * - buffer_ptr is reinterpreted as HUTT JSON (string or object)
 * - buffer_size is ignored (not needed for JavaScript objects)
 * - Uses previousTargetDataStore pattern: first call saves state, subsequent calls animate
 *
 * @param handle - The controller handle
 * @param datastoreJson - HUTT JSON datastore (string or object) - the TARGET (end) state
 * @param buffer_size - Size of datastore (ignored in TypeScript, kept for API compatibility)
 * @param animationStyleIntVal - Animation style (0=Synced, 1=Phased, 2=Inherit)
 * @param animationDuration - Duration multiplier
 * @returns true if animation started successfully
 */
export function _AnimateDatastore(
  handle: number,
  datastoreInput: any, // HUTT JavaScript object (decoded by viz-client)
  buffer_size: number, // Kept for API compatibility, ignored in TypeScript
  animationStyleIntVal: number,
  animationDuration: number
): boolean {
  const targetDataStore = datastoreInput;
  const playStyle = AnimationPlayStyle.OneShot;
  const durationMs = DEFAULT_ANIMATION_DURATION_MS * animationDuration;

  const animationStyle = animationStyleFromInt(animationStyleIntVal);
  const success = MarkAnimationController.startAnimationWithTarget(handle, targetDataStore, durationMs, playStyle, animationStyle);
  return success;
}

/**
 * Tick the animation system forward.
 *
 * WASM equivalent: _Tick(double seconds)
 *
 * @param seconds - Current timestamp in seconds
 * @returns true if any controller has frames ready
 */
export function _Tick(seconds: number): boolean {
  // Convert seconds to milliseconds for our tick function
  const timestamp = seconds * 1000;
  const hasFrames = tick(timestamp);
  return hasFrames;
}

/**
 * Check if a controller has an animation frame ready.
 *
 * WASM equivalent: _AnimationFrameReady(int handle)
 *
 * @param handle - The controller handle
 * @returns true if frame is ready
 */
export function _AnimationFrameReady(handle: number): boolean {
  const ready = MarkAnimationController.hasAnimationFrame(handle);
  return ready;
}

/**
 * Get the animation frame from a controller.
 *
 * WASM equivalent: _GetAnimationFrame(int handle) → returns int pointer to HUTT on heap
 *
 * In WASM version:
 * - Returns a pointer to marshalled HUTT data on the WASM heap
 * - Viz-client then unmarshalls it with JsHeapMarshaller
 *
 * In TypeScript version:
 * - We return the IDataStore directly as a JavaScript object
 * - Viz-client will need to handle this differently
 * - TODO: Figure out if we need to convert IDataStore → specific format
 *
 * @param handle - The controller handle
 * @returns The animation frame (for now, returning the IDataStore object directly)
 */
export function _GetAnimationFrame(handle: number): any {
  const frame = MarkAnimationController.getAnimationFrame(handle);

  if (frame === null) {
    return null;
  }

  // Convert IDataStore to plain JavaScript object (HUTT format)
  // frame.toObject() returns the underlying data with interpolated values applied
  const frameData = frame.toObject();
  return frameData;
}

/**
 * Replay an animation.
 *
 * WASM equivalent: _ReplayAnimation(int handle)
 *
 * Note: Uses the global replay speed set via _SetReplaySpeed().
 * Matches C++ pattern where replay speed is stored globally in AnimationManager
 * and applied during replay.
 *
 * @param handle - The controller handle
 */
export function _ReplayAnimation(handle: number): void {
  // Call without speed parameter - will use stored global replay speed
  MarkAnimationController.replayAnimation(handle);
}

/**
 * Set replay speed.
 *
 * WASM equivalent: _SetReplaySpeed(double replaySpeed)
 *
 * Stores the global replay speed that will be used for subsequent replays.
 * Matches C++ AnimationManager::SetReplaySpeed() (line 490-493)
 *
 * @param replaySpeed - The replay speed multiplier (0.5 = half, 1.0 = actual, 2.0 = double)
 */
export function _SetReplaySpeed(replaySpeed: number): void {
  MarkAnimationController.setReplaySpeed(replaySpeed);
}

/**
 * Get replay speed.
 *
 * WASM equivalent: _GetReplaySpeed() → double
 *
 * Returns the current global replay speed.
 * Matches C++ AnimationManager::GetReplaySpeed() (line 485-488)
 *
 * @returns The current replay speed multiplier
 */
export function _GetReplaySpeed(): number {
  return MarkAnimationController.getReplaySpeed();
}

/**
 * Check if controller has animation to replay.
 *
 * WASM equivalent: _GetHasAnimationToReplay(int handle)
 *
 * @param handle - The controller handle
 * @returns true if animation can be replayed
 */
export function _GetHasAnimationToReplay(handle: number): boolean {
  return MarkAnimationController.getHasAnimationToReplay(handle);
}

/**
 * Set sheet ID for a controller.
 *
 * WASM equivalent: _SetSheetId(int handle, object sheetId)
 *
 * @param handle - The controller handle
 * @param sheetId - The sheet ID (string - in WASM this is a heap pointer, in TypeScript it's a direct string)
 */
export function _SetSheetId(handle: number, sheetId: any): void {
  // Convert to string if needed (viz-client might pass various types)
  const sheetIdStr = String(sheetId);
  MarkAnimationController.setSheetId(handle, sheetIdStr);
}

/**
 * Set current group key.
 *
 * WASM equivalent: _SetCurrentGroupKey(object key)
 *
 * @param key - The group key (string - in WASM this is a heap pointer, in TypeScript it's a direct string)
 */
export function _SetCurrentGroupKey(key: any): void {
  // Convert to string if needed (viz-client might pass various types)
  const keyStr = String(key);
  MarkAnimationController.setCurrentGroupKey(keyStr);
}

/**
 * Delete a group key.
 *
 * WASM equivalent: _DeleteGroupKey(object key)
 *
 * @param key - The group key to delete (string - in WASM this is a heap pointer, in TypeScript it's a direct string)
 */
export function _DeleteGroupKey(key: any): void {
  // Convert to string if needed (viz-client might pass various types)
  const keyStr = String(key);
  MarkAnimationController.deleteGroupKey(keyStr);
}

/**
 * Delete all group keys.
 *
 * WASM equivalent: _DeleteAllGroupKeys()
 *
 * Mirrors C++ PlayedAnimationTracker::DeleteAllGroupKeys()
 */
export function _DeleteAllGroupKeys(): void {
  MarkAnimationController.deleteAllGroupKeys();
}

/**
 * Reset a MarkAnimationController.
 *
 * WASM equivalent: _ResetMarkAnimationController(int handle)
 *
 * @param handle - The controller handle
 */
export function _ResetMarkAnimationController(handle: number): void {
  MarkAnimationController.resetController(handle);
}

/**
 * Set previous target datastore.
 *
 * This is called by viz-client to tell the animation module what the CURRENT visual state is,
 * so the next animation knows where to animate FROM.
 *
 * WASM equivalent: _SetPreviousTargetDataStore(int handle, int buffer_ptr, int buffer_size)
 *
 * @param handle - The controller handle
 * @param buffer_ptr - Pointer to datastore buffer (hex string in TypeScript mode)
 * @param buffer_size - Size of buffer
 */
export function _SetPreviousTargetDataStore(handle: number, datastoreInput: any, _buffer_size: number): void {
  // datastoreInput is already a HUTT JavaScript object (decoded by viz-client), same as _AnimateDatastore
  MarkAnimationController.setPreviousTargetDataStore(handle, datastoreInput);
}

// ============================================
// Telemetry Functions (for AnimationLogData)
// ============================================
// These are called by viz-client for telemetry/debugging

export function _GetTickDurationCount(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetAverageTickDurationInMilliseconds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetLongestTickDurationInMilliseconds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetAnimationSetUpTimeInMilliseconds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetTickDurationInMillisecondsAtIndex(_handle: number, _index: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetTotalNumberOfEnterMarkIds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetTotalNumberOfExitMarkIds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetTotalNumberOfMoveMarkIds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfSubPaneRows(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfSubPaneColumns(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfUserPanes(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfPaneDescriptorIds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetOverallAnimationDurationForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetPrimTypeForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetAnimationStyleIntegerForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetScenarioTypeIntegerForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfEnterMarkIdsForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfExitMarkIdsForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetNumberOfMoveMarkIdsForPaneAtIndex(_handle: number, _paneDescriptorIdIndex: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetAnimationDurationMultiplier(_handle: number): number {
  return 1.0; // TODO: Implement telemetry
}

export function _GetDSReadDurationInMilliseconds(_handle: number): number {
  return 0; // TODO: Implement telemetry
}

export function _GetJSHeapMarshallingDurationInMilliseconds(_handle: number, _index: number): number {
  return 0; // TODO: Implement telemetry
}
